export type Tab<T extends string = string> = {
  key: T;
  label: string;
  count?: number | null;
};

type TabsProps<T extends string = string> = {
  tabs: Tab<T>[];
  value: T;
  onChange: (next: T) => void;
};

/**
 * Horizontal tab strip modeled on the Apple Search Ads web UI — active
 * tab gets an accent-coloured label + underline, inactive tabs are
 * muted. Counts render as a trailing muted number when provided.
 *
 * Sits directly above a table. Caller renders the table body based on
 * `value`.
 */
export function Tabs<T extends string = string>({ tabs, value, onChange }: TabsProps<T>) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        gap: 0,
        borderBottom: "1px solid var(--c-border)",
        marginBottom: 0,
      }}
    >
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            style={{
              background: "transparent",
              border: 0,
              padding: "14px 18px",
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--c-accent-600)" : "var(--c-text-3)",
              cursor: "pointer",
              position: "relative",
              letterSpacing: "-0.005em",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              transition: "color .15s var(--ease)",
            }}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.color = "var(--c-text)";
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.color = "var(--c-text-3)";
            }}
          >
            <span>{t.label}</span>
            {t.count !== undefined && t.count !== null && (
              <span
                style={{
                  fontSize: 11.5,
                  color: "var(--c-text-4)",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 500,
                }}
              >
                {t.count}
              </span>
            )}
            {active && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: 14,
                  right: 14,
                  bottom: -1,
                  height: 2,
                  background: "var(--c-accent-600)",
                  borderRadius: "1px 1px 0 0",
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
